/* The application shell and router (app.ts render() ~5260 + the static
   index.html layout). It reproduces the vanilla dispatch by state.view, wraps
   everything in the store provider, kicks off the data load, and lays out the
   same DOM the static page had: .hero, .layout > (#drawer, #drawer-scrim,
   .wrap > #navigator, #live, then the current view's #banner/#sections/#footnote),
   and #picker. Every non-shell view is a component in ../views; the surface
   agents fill those. The intake view mounts the EXISTING <IntakeApp/> directly
   (client-framework.md CF6). */
import type { JSX } from "react";
import { AppProvider, useAppState } from "./store";
import { useHermesData } from "./useHermesData";
import { Hero } from "./Hero";
import { Drawer } from "./Drawer";
import { Navigator } from "./Navigator";
import { Picker } from "./Picker";
import { Loading } from "./Loading";
import { ViewFrame } from "./ViewFrame";
import { IntakeApp } from "../intake/IntakeApp";
import { ProjectsView } from "../views/ProjectsView";
import { ProjectFormView } from "../views/ProjectFormView";
import { ProjectAreaView } from "../views/ProjectAreaView";
import { TeamsView } from "../views/TeamsView";
import { TeamAdminView } from "../views/TeamAdminView";
import { PermissionsView } from "../views/PermissionsView";
import { RolesView } from "../views/RolesView";
import { PdpView } from "../views/PdpView";
import { ProductsView } from "../views/ProductsView";
import { ProductAreaView } from "../views/ProductAreaView";
import { AboutView } from "../views/AboutView";
import { HomeView } from "../views/HomeView";
import { IssuesView } from "../views/IssuesView";

/* The current view, dispatched exactly as render() dispatched (app.ts ~5281):
   an area page replaces the record list, never the forms (N17). */
function CurrentView(): JSX.Element {
  const { state } = useAppState();
  /* Before /api/me answers, the surface — and so the landing (MA2) — is not
     known: commit to NO section, one neutral skeleton (app.ts render guard). */
  if (state.mePending && !state.navigated) return <Loading what="your workspace" />;

  const v = state.view;
  if (state.area && v === "projects") return <ProjectAreaView />;
  if (v === "teams") return <TeamsView />;
  if (v === "team-admin") return <TeamAdminView />;
  if (v === "project-form") return <ProjectFormView />;
  if (v === "permissions") return <PermissionsView />;
  if (v === "roles") return <RolesView />;
  if (v === "pdp") return <PdpView />;
  /* a product record is its areas (products-pdp.md PP15); no record open =
     the section's table */
  if (v === "products") return state.area && state.openProductId ? <ProductAreaView /> : <ProductsView />;
  /* the intake module supplies its own MODULE BAR (intake/nav.tsx), so it
     renders with empty banner/footnote and no navigator (renderIntake, per
     Lewis Aug 31). The masthead is still the shell's `Hero`, and there is no
     domain tab strip any more — one form, one queue (UF8). */
  if (v === "intake")
    return (
      <ViewFrame>
        <IntakeApp />
      </ViewFrame>
    );
  if (v === "about-hermes") return <AboutView />;
  if (v === "home") return <HomeView />;
  /* the issue tracker (issue-tracker.md IT9) */
  if (v === "issues") return <IssuesView />;
  return <ProjectsView />;
}

function Shell(): JSX.Element {
  useHermesData();
  const { state } = useAppState();
  /* the navigator is suppressed in the same two cases render() suppressed it:
     the pre-me workspace skeleton, and the self-branded intake module */
  const preMe = state.mePending && !state.navigated;
  const showNavigator = !preMe && state.view !== "intake";
  return (
    <>
      <Hero />
      <div className="layout" inert={state.pickerOpen || undefined}>
        <Drawer />
        <div className="wrap">
          {showNavigator ? <Navigator /> : <div id="navigator" hidden />}
          {/* the one persistent polite live region (MA16) — never replaced */}
          <div id="live" role="status" aria-live="polite" className="sr-only" />
          <CurrentView />
        </div>
      </div>
      <Picker />
    </>
  );
}

export function App(): JSX.Element {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
